import { useCallback, useRef, useState } from 'react'
import type { ComplianceDocumentSectionRecord } from '@artem-lex/compliance-documents'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import type {
  PackagingComponent,
  PackagingRecyclabilityAssessmentStoredEntry,
} from '@/shared/hooks/api/packagingSuppliers'
import {
  useExportPackagingRecyclabilityAssessmentDocumentPdf,
  useGeneratePackagingRecyclabilityAssessmentDocumentProse,
} from '@/shared/hooks/api/packagingSuppliers'
import { buildRecyclabilityAssessmentDocumentSections } from './buildRecyclabilityAssessmentDocumentMarkdown'
import { buildPackagingRecyclabilityPdfCoverMarkdown } from './buildPackagingRecyclabilityPdfCoverMarkdown'
import {
  RecyclabilityAssessmentDocumentEditor,
  type RecyclabilityAssessmentDocumentEditorHandle,
} from './RecyclabilityAssessmentDocumentEditor'
import type { RecyclabilityAssessmentDocumentAnnex1Prose } from './recyclabilityAssessmentDocumentAnnex1Helpers'
import type { RecyclabilityAssessmentDocumentProseByCriterion } from './recyclabilityAssessmentDocumentSection3Helpers'
import { usePackagingSuppliersComponentEditorLabels } from './usePackagingSuppliersComponentEditorLabels'

type PackagingItemRecyclabilityAssessmentDocumentSheetProps = {
  supplierId: string
  itemId: string
  itemName: string
  itemDescription: string
  intendedUse: string
  packagingIdentificationNumber: string
  manufacturerName: string
  manufacturerAddress: string
  components: PackagingComponent[]
  history: PackagingRecyclabilityAssessmentStoredEntry[] | undefined
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function PackagingItemRecyclabilityAssessmentDocumentSheet({
  supplierId,
  itemId,
  itemName,
  itemDescription,
  intendedUse,
  packagingIdentificationNumber,
  manufacturerName,
  manufacturerAddress,
  components,
  history,
  open,
  onOpenChange,
}: PackagingItemRecyclabilityAssessmentDocumentSheetProps) {
  const { t, i18n } = useTranslation('packagingSuppliers')
  const labels = usePackagingSuppliersComponentEditorLabels()
  const editorRef = useRef<RecyclabilityAssessmentDocumentEditorHandle>(null)
  const [generatedProse, setGeneratedProse] =
    useState<RecyclabilityAssessmentDocumentProseByCriterion | null>(null)
  const [annex1Prose, setAnnex1Prose] =
    useState<RecyclabilityAssessmentDocumentAnnex1Prose | null>(null)
  const [sections, setSections] = useState<ComplianceDocumentSectionRecord[]>(
    []
  )
  const [coverMarkdown, setCoverMarkdown] = useState<string | null>(null)
  const [contentSyncKey, setContentSyncKey] = useState(0)
  const [coverContentSyncKey, setCoverContentSyncKey] = useState(0)
  const proseMutation = useGeneratePackagingRecyclabilityAssessmentDocumentProse(
    supplierId,
    itemId
  )
  const exportPdfMutation = useExportPackagingRecyclabilityAssessmentDocumentPdf(
    supplierId,
    itemId
  )
  const { mutateAsync, reset, isPending, isError } = proseMutation

  const hasGeneratedDocument =
    generatedProse != null &&
    annex1Prose != null &&
    sections.length > 0

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen)
    if (!nextOpen) {
      setGeneratedProse(null)
      setAnnex1Prose(null)
      setSections([])
      setCoverMarkdown(null)
      setContentSyncKey(0)
      setCoverContentSyncKey(0)
      reset()
      exportPdfMutation.reset()
    }
  }

  const runDocumentGeneration = useCallback(async () => {
    setGeneratedProse(null)
    setAnnex1Prose(null)
    setSections([])
    setCoverMarkdown(null)
    try {
      const response = await mutateAsync()
      const assessmentTimestamp = history?.[0]?.timestamp
      const nextCoverMarkdown = buildPackagingRecyclabilityPdfCoverMarkdown({
        translate: t,
        locale: i18n.language,
        itemName,
        itemDescription,
        intendedUse,
        packagingIdentificationNumber,
        manufacturerName: manufacturerName ?? '',
        manufacturerAddress: manufacturerAddress ?? '',
        reportReferenceNo: packagingIdentificationNumber.trim(),
        dateOfAssessmentIso: assessmentTimestamp,
        components,
        history,
      })
      const nextSections = buildRecyclabilityAssessmentDocumentSections({
        translate: t,
        locale: i18n.language,
        itemName,
        components,
        history,
        labels,
        generatedProse: response.prose,
        annex1Prose: response.annex1,
      })
      setGeneratedProse(response.prose)
      setAnnex1Prose(response.annex1)
      setCoverMarkdown(nextCoverMarkdown)
      setSections(nextSections)
      setContentSyncKey((key) => key + 1)
      setCoverContentSyncKey((key) => key + 1)
    } catch {
      // isError drives the error message.
    }
  }, [
    components,
    history,
    i18n.language,
    itemDescription,
    intendedUse,
    itemName,
    labels,
    manufacturerAddress,
    manufacturerName,
    mutateAsync,
    packagingIdentificationNumber,
    t,
  ])

  const handleExportPdf = useCallback(async () => {
    if (editorRef.current == null) {
      return
    }
    const flushedSections = editorRef.current.flushSections()
    const flushedCover = editorRef.current.flushCoverMarkdown()
    const slug = itemName.trim().replace(/\s+/g, '-').slice(0, 80)
    try {
      await exportPdfMutation.mutateAsync({
        sections: flushedSections,
        coverMarkdown: flushedCover ?? undefined,
        documentName:
          slug.length > 0
            ? `recyclability-assessment-${slug}.pdf`
            : undefined,
      })
    } catch {
      // mutation isError drives messaging if we add it
    }
  }, [exportPdfMutation, itemName])

  const isExportPending = exportPdfMutation.isPending

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side='right'
        className='flex h-full w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-4xl'
      >
        <SheetHeader className='shrink-0 border-b px-6 py-4 pe-10 text-start'>
          <SheetTitle>
            {t('itemDetail.recyclability.document.sheetTitle')}
          </SheetTitle>
          <SheetDescription>
            {t('itemDetail.recyclability.document.sheetDescription')}
          </SheetDescription>
        </SheetHeader>

        <div className='min-h-0 flex-1 overflow-y-auto px-6 py-4'>
          {!hasGeneratedDocument && isPending ? (
            <div className='flex min-h-[50dvh] flex-col items-center justify-center gap-3 text-muted-foreground'>
              <Loader2 className='size-8 animate-spin' aria-hidden />
              <p className='text-sm'>
                {t('itemDetail.recyclability.document.section3.generatingProse')}
              </p>
            </div>
          ) : null}
          {!hasGeneratedDocument && isError && !isPending ? (
            <div className='flex min-h-[50dvh] flex-col items-center justify-center gap-3 text-center'>
              <p className='text-sm text-destructive'>
                {t('itemDetail.recyclability.document.section3.generateProseFailed')}
              </p>
            </div>
          ) : null}
          {hasGeneratedDocument ? (
            <RecyclabilityAssessmentDocumentEditor
              ref={editorRef}
              sections={sections}
              contentSyncKey={contentSyncKey}
              coverMarkdown={coverMarkdown}
              coverContentSyncKey={coverContentSyncKey}
            />
          ) : null}
          {hasGeneratedDocument && exportPdfMutation.isError ? (
            <p className='mt-4 text-sm text-destructive'>
              {t('itemDetail.recyclability.document.exportPdfFailed')}
            </p>
          ) : null}
        </div>

        <SheetFooter className='shrink-0 gap-2 border-t px-6 py-4 sm:flex-row sm:justify-end'>
          {!hasGeneratedDocument ? (
            <Button
              type='button'
              variant='outline'
              disabled={isPending}
              onClick={() => {
                void runDocumentGeneration()
              }}
            >
              {isPending
                ? t('itemDetail.recyclability.document.section3.generatingProse')
                : t('itemDetail.recyclability.document.section3.generateProse')}
            </Button>
          ) : (
            <Button
              type='button'
              variant='outline'
              disabled={isExportPending || isPending}
              onClick={() => {
                void handleExportPdf()
              }}
            >
              {isExportPending
                ? t('itemDetail.recyclability.document.exportingPdf')
                : t('itemDetail.recyclability.document.exportPdf')}
            </Button>
          )}
          <Button
            type='button'
            onClick={() => {
              handleOpenChange(false)
            }}
          >
            {t('common:actions.close')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
